import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, RefreshCw, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminBulkOpsPage() {
  const navigate = useNavigate();
  const { user, token, isAuthenticated, loading: authLoading } = useAuth();

  const [running, setRunning] = useState(null);
  const [result, setResult] = useState(null);
  const [confirmText, setConfirmText] = useState("");

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || user?.role !== "admin") navigate("/");
  }, [authLoading, isAuthenticated, user, navigate]);

  const handleConvertPremium = async () => {
    if (confirmText !== "CONFIRM") {
      toast.error('확인란에 "CONFIRM" 을 입력해주세요');
      return;
    }
    if (!window.confirm("정말로 모든 프리미엄(premium) 회원과 잘못 변환된 standard 회원을 정회원(regular)으로 변경하시겠습니까?\n이 작업은 되돌리기 어렵습니다.")) return;

    setRunning("premium");
    setResult(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/users/convert-premium-to-standard`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "실행 실패");
      setResult({ op: "premium", ...d });
      toast.success(d.message || `${d.converted}명 변경 완료`);
      setConfirmText("");
    } catch (e) {
      toast.error(e.message);
      setResult({ op: "premium", error: e.message });
    } finally {
      setRunning(null);
    }
  };

  if (authLoading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-7 h-7 animate-spin text-burgundy-500" /></div>;
  }

  return (
    <div className="min-h-screen bg-stone-50 pt-20 pb-12">
      <div className="max-w-3xl mx-auto px-4">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate("/admin")} className="p-2 rounded-lg hover:bg-stone-100">
            <ArrowLeft className="w-5 h-5 text-stone-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-stone-900 flex items-center gap-2">
              <RefreshCw className="w-6 h-6 text-burgundy-600" />
              회원 일괄 작업
            </h1>
            <p className="text-sm text-stone-500">대량 회원 데이터 일괄 변환·정리 도구</p>
          </div>
        </div>

        {/* Premium → Regular */}
        <div className="bg-white rounded-2xl border border-stone-200 p-6">
          <h2 className="font-bold text-stone-800 mb-2">프리미엄 → 정회원 일괄 변경</h2>
          <p className="text-sm text-stone-500 mb-4">
            <code className="bg-stone-100 px-1.5 py-0.5 rounded text-stone-700">membership_type: "premium"</code> 또는 과거 잘못 변환된{" "}
            <code className="bg-stone-100 px-1.5 py-0.5 rounded text-stone-700">"standard"</code> 회원을 모두{" "}
            <code className="bg-blue-100 px-1.5 py-0.5 rounded text-blue-700">"regular"</code> (정회원) 로 정정합니다.
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 space-y-1 mb-4">
            <p className="font-semibold flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> 실행 전 확인사항
            </p>
            <ul className="list-disc list-inside text-[13px] space-y-0.5 ml-1">
              <li>전체 프리미엄 회원에 일괄 적용됩니다 (선택 불가)</li>
              <li>VIP, 일반(basic) 회원은 변경되지 않습니다</li>
              <li>admin 권한은 그대로 유지됩니다</li>
              <li>실행 후 즉시 회원 목록·관리자 페이지에 반영됩니다</li>
            </ul>
          </div>

          <div className="space-y-3">
            <div>
              <label className="text-sm font-semibold text-stone-700 mb-1 block">
                확인 — 정확히 <code className="bg-stone-100 px-1.5 py-0.5 rounded">CONFIRM</code> 입력
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder='"CONFIRM" 입력'
                className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-red-300"
                data-testid="bulk-confirm-input"
              />
            </div>

            <Button
              onClick={handleConvertPremium}
              disabled={running === "premium" || confirmText !== "CONFIRM"}
              className="w-full bg-burgundy-600 hover:bg-burgundy-700 text-white disabled:opacity-40"
              data-testid="convert-premium-btn"
            >
              {running === "premium" ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> 변경 중...</>
              ) : (
                <><RefreshCw className="w-4 h-4 mr-2" /> 프리미엄 → 정회원 일괄 변경 실행</>
              )}
            </Button>
          </div>

          {result?.op === "premium" && (
            <div className={`mt-5 rounded-xl border p-4 ${result.error ? "bg-red-50 border-red-200" : "bg-emerald-50 border-emerald-200"}`}>
              {result.error ? (
                <p className="text-sm text-red-700 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" /> {result.error}
                </p>
              ) : (
                <>
                  <p className="text-emerald-700 font-bold flex items-center gap-2 mb-2">
                    <CheckCircle2 className="w-5 h-5" /> {result.message}
                  </p>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div className="bg-white rounded p-2 border border-emerald-200">
                      <p className="text-[11px] text-stone-500">변경 전 프리미엄</p>
                      <p className="text-lg font-bold">{result.premium_before}</p>
                    </div>
                    <div className="bg-white rounded p-2 border border-emerald-200 ring-2 ring-emerald-300">
                      <p className="text-[11px] text-stone-500">✅ 변경된 수</p>
                      <p className="text-lg font-bold text-emerald-700">{result.converted}</p>
                    </div>
                    <div className="bg-white rounded p-2 border border-emerald-200">
                      <p className="text-[11px] text-stone-500">현재 정회원 총수</p>
                      <p className="text-lg font-bold">{result.standard_total_now}</p>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
